import { Alert, Col, Input, Modal, Progress, Row, Select, Space, Tag, Tooltip, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import React, { Component } from "react";
import { connect } from "react-redux";
import { ButtonItem, DatePickerItem, ModalItem, TableItem } from "../../../../Components/Items";
import { mostrarResumenImportacion } from "../../../../Components/importarAsignacionesUI";
import {
  claveSerieNumero,
  nombreEmpleado,
  normalizarTexto,
} from "../../../../Components/importarAsignaciones.utils";
import {
  etiquetaTurno,
  prepararCatalogos,
} from "../ImportarAsignacionTurnos/plantillaAsignacionTurnos";

// Las reglas (crear, reactivar, extender, recortar el turno anterior...) viven en
// el backend, en Utils/planAsignacionTurnos.utils.js. La revisión es ese mismo
// endpoint con `simular: true`, así que lo que se ve es lo que se aplica.

// Valor del filtro para los empleados sin departamento / sin puesto: el Select no
// se lleva bien con "" como valor de una opción.
const SIN_VALOR = "__sin__";

const TAMANO_BLOQUE = 50;

const labelStyle = { display: "block", fontSize: 11, color: "#888", marginBottom: 3 };

const OPERACIONES = {
  crear: { texto: "Crear", color: "green" },
  reactivar: { texto: "Reactivar", color: "blue" },
  extender: { texto: "Extender", color: "cyan" },
  sinCambios: { texto: "Ya asignado", color: "default" },
  error: { texto: "Error", color: "red" },
};

const SE_APLICAN = new Set(["crear", "reactivar", "extender"]);

const ESTADO_INICIAL = {
  cargando: false,
  catalogos: null,
  turnoId: undefined,
  inicio: null,
  fin: null,
  departamentos: [],
  puestos: [],
  busqueda: "",
  seleccionados: [],
  revisando: false,
  etapa: "seleccion",
  plan: [],
  enviando: false,
  progreso: null,
};

const codigoTexto = (empleado) =>
  empleado?.codigo === undefined || empleado?.codigo === null
    ? ""
    : String(empleado.codigo).padStart(4, "0");

// Las fechas guardadas son días; se comparan como "YYYY-MM-DD" en la hora del
// navegador, que es el mismo día que pinta la tabla de Asignación de Turnos.
const claveDia = (valor) => (valor ? moment(valor).format("YYYY-MM-DD") : "");
const fechaTexto = (valor) => (valor ? moment(valor).format("DD/MM/YYYY") : "");
const claveATexto = (clave) => (clave ? moment(clave, "YYYY-MM-DD").format("DD/MM/YYYY") : "");

const resumirPlan = (plan) => {
  const resumen = {
    crear: 0,
    reactivar: 0,
    extender: 0,
    sinCambios: 0,
    error: 0,
    recortes: 0,
    deshabilitados: 0,
    conAviso: 0,
  };
  for (const fila of plan) {
    resumen[fila.operacion] = (resumen[fila.operacion] || 0) + 1;
    if (!SE_APLICAN.has(fila.operacion)) continue;
    for (const ajuste of fila.ajustes || []) {
      if (ajuste.accion === "recortar") resumen.recortes += 1;
      else resumen.deshabilitados += 1;
    }
    if ((fila.avisos || []).length) resumen.conAviso += 1;
  }
  return resumen;
};

class AgregarAsignacionesTurnosMasivas extends Component {
  state = { ...ESTADO_INICIAL };

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({ ...ESTADO_INICIAL });
      this.consultarCatalogos();
    }
  }

  consultarCatalogos() {
    this.setState({ cargando: true });
    axios
      .post(
        "asignacionesTurnos/datosParaAsignacionMasivaTurnos",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({ cargando: false, catalogos: prepararCatalogos(res.data) });
        } else {
          this.setState({ cargando: false });
          message.error(res.data.message || "No se pudieron cargar los empleados y turnos");
        }
      })
      .catch(() => {
        this.setState({ cargando: false });
        message.error("Error de conexión al cargar los empleados y turnos");
      });
  }

  cerrar() {
    // Con un envío a medias no se puede cerrar: los bloques siguientes se
    // seguirían mandando sin que nadie vea el resultado.
    if (this.state.enviando) return;
    this.props.cerrarAgregarAsignacionesTurnosMasivas();
  }

  rango() {
    const { inicio, fin } = this.state;
    return {
      inicio: inicio ? inicio.format("YYYY-MM-DD") : "",
      fin: fin ? fin.format("YYYY-MM-DD") : "",
    };
  }

  errorFechas() {
    const { inicio, fin } = this.rango();
    return inicio && fin && inicio > fin ? "La fecha de fin es anterior a la de inicio" : null;
  }

  // ─── Selección ─────────────────────────────────────────────────────────────

  opcionesDe(campo, etiquetaVacia) {
    const empleados = this.state.catalogos?.empleados || [];
    const valores = new Set(empleados.map((empleado) => empleado[campo] || SIN_VALOR));
    return [...valores]
      .map((valor) => ({ value: valor, label: valor === SIN_VALOR ? etiquetaVacia : valor }))
      .sort((a, b) => {
        if (a.value === SIN_VALOR) return 1;
        if (b.value === SIN_VALOR) return -1;
        return a.label.localeCompare(b.label);
      });
  }

  filasDeEmpleados() {
    const { catalogos, departamentos, puestos, busqueda, turnoId } = this.state;
    if (!catalogos) return [];

    const textoBuscado = normalizarTexto(busqueda);
    const { inicio, fin } = this.rango();
    const conRango = Boolean(inicio && fin && inicio <= fin);

    return catalogos.empleados
      .filter(
        (empleado) =>
          departamentos.length === 0 || departamentos.includes(empleado.departamento || SIN_VALOR)
      )
      .filter((empleado) => puestos.length === 0 || puestos.includes(empleado.puesto || SIN_VALOR))
      .map((empleado) => {
        const activas = (catalogos.porEmpleado.get(String(empleado._id)) || [])
          .filter((asignacion) => Number(asignacion.estado) === 1)
          .filter(
            (asignacion) =>
              !conRango || (claveDia(asignacion.inicio) <= fin && claveDia(asignacion.fin) >= inicio)
          )
          .sort((a, b) => claveDia(a.inicio).localeCompare(claveDia(b.inicio)));

        // Ya tiene ese turno activo cubriendo todo el rango: no hay nada que hacer.
        const cubierto =
          conRango &&
          Boolean(turnoId) &&
          activas.some(
            (asignacion) =>
              String(asignacion.turnoId) === turnoId &&
              claveDia(asignacion.inicio) <= inicio &&
              claveDia(asignacion.fin) >= fin
          );

        return {
          _id: String(empleado._id),
          codigo: codigoTexto(empleado),
          numero: claveSerieNumero(empleado),
          nombre: nombreEmpleado(empleado),
          departamento: empleado.departamento || "",
          puesto: empleado.puesto || "",
          activas: activas.map((asignacion) => ({
            _id: String(asignacion._id),
            mismo: String(asignacion.turnoId) === turnoId,
            turno: etiquetaTurno(catalogos.turnoPorId.get(String(asignacion.turnoId))),
            fechas: `${fechaTexto(asignacion.inicio)} al ${fechaTexto(asignacion.fin)}`,
          })),
          cubierto,
        };
      })
      .filter(
        (fila) =>
          !textoBuscado ||
          normalizarTexto(`${fila.nombre} ${fila.codigo} ${fila.numero}`).includes(textoBuscado)
      )
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }

  seleccionarFiltrados(filas) {
    const ids = new Set(this.state.seleccionados);
    for (const fila of filas) {
      if (!fila.cubierto) ids.add(fila._id);
    }
    this.setState({ seleccionados: [...ids] });
  }

  revisar() {
    const { turnoId, seleccionados } = this.state;
    const { inicio, fin } = this.rango();

    this.setState({ revisando: true });
    axios
      .post(
        "asignacionesTurnos/asignarTurnosMasivo",
        {
          empresaId: this.props.empresaId,
          turnoId,
          inicio,
          fin,
          empleadoIds: seleccionados,
          simular: true,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({ revisando: false, etapa: "revision", plan: res.data.plan || [] });
        } else {
          this.setState({ revisando: false });
          message.error(res.data.message || "No se pudo revisar la asignación");
        }
      })
      .catch(() => {
        this.setState({ revisando: false });
        message.error("Error de conexión al revisar la asignación");
      });
  }

  renderSeleccion() {
    const {
      catalogos,
      turnoId,
      inicio,
      fin,
      departamentos,
      puestos,
      busqueda,
      seleccionados,
      revisando,
    } = this.state;

    const errorFechas = this.errorFechas();
    const filas = this.filasDeEmpleados();
    const seleccionablesFiltrados = filas.filter((fila) => !fila.cubierto).length;
    const rangoCompleto = Boolean(inicio && fin && !errorFechas);

    const opcionesTurnos = (catalogos?.turnos || [])
      .filter((turno) => Number(turno.estado) === 1)
      .map((turno) => ({ value: String(turno._id), label: etiquetaTurno(turno) }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const columnas = [
      { title: "Código", dataIndex: "codigo", key: "codigo", width: 80 },
      { title: "Número", dataIndex: "numero", key: "numero", width: 100 },
      { title: "Empleado", dataIndex: "nombre", key: "nombre" },
      { title: "Departamento", dataIndex: "departamento", key: "departamento" },
      { title: "Puesto", dataIndex: "puesto", key: "puesto" },
      {
        title: rangoCompleto ? "Turnos activos en esas fechas" : "Turnos activos",
        dataIndex: "activas",
        key: "activas",
        render: (activas) =>
          (activas || []).map((activa) => (
            <Tooltip key={activa._id} title={`Del ${activa.fechas}`}>
              <Tag color={activa.mismo ? "green" : "orange"}>{activa.turno}</Tag>
            </Tooltip>
          )),
      },
    ];

    return (
      <>
        <Row gutter={12} style={{ marginBottom: 10 }}>
          <Col xs={24} md={12}>
            <label style={labelStyle}>Turno</label>
            <Select
              showSearch
              allowClear
              style={{ width: "100%" }}
              placeholder="Elige el turno"
              options={opcionesTurnos}
              optionFilterProp="label"
              value={turnoId}
              onChange={(valor) => this.setState({ turnoId: valor })}
            />
          </Col>
          <Col xs={12} md={6}>
            <label style={labelStyle}>Inicio</label>
            <DatePickerItem
              placeholder="Fecha de inicio"
              width="100%"
              value={inicio}
              onChange={(valor) => this.setState({ inicio: valor || null })}
            />
          </Col>
          <Col xs={12} md={6}>
            <label style={labelStyle}>Fin</label>
            <DatePickerItem
              placeholder="Fecha de fin"
              width="100%"
              value={fin}
              onChange={(valor) => this.setState({ fin: valor || null })}
            />
            {errorFechas && (
              <div style={{ fontSize: 11, color: "#ff4d4f", marginTop: 4 }}>{errorFechas}</div>
            )}
          </Col>
        </Row>

        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 10 }}>
          Si un empleado ya tiene otro turno activo en esas fechas, el anterior se recorta para que
          termine un día antes del nuevo; si queda completo dentro de las fechas nuevas, se
          deshabilita. En la revisión ves exactamente qué le pasa a cada uno antes de aplicar.
        </div>

        <Row gutter={12} style={{ marginBottom: 10 }}>
          <Col xs={24} md={8}>
            <label style={labelStyle}>Departamento</label>
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              placeholder="Todos"
              options={this.opcionesDe("departamento", "(Sin departamento)")}
              optionFilterProp="label"
              value={departamentos}
              onChange={(valor) => this.setState({ departamentos: valor })}
            />
          </Col>
          <Col xs={24} md={8}>
            <label style={labelStyle}>Puesto</label>
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              placeholder="Todos"
              options={this.opcionesDe("puesto", "(Sin puesto)")}
              optionFilterProp="label"
              value={puestos}
              onChange={(valor) => this.setState({ puestos: valor })}
            />
          </Col>
          <Col xs={24} md={8}>
            <label style={labelStyle}>Buscar</label>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Nombre, código o número"
              value={busqueda}
              onChange={(e) => this.setState({ busqueda: e.target.value })}
            />
          </Col>
        </Row>

        <Space style={{ marginBottom: 8 }} wrap>
          <ButtonItem
            type="default"
            size="small"
            disabled={seleccionablesFiltrados === 0}
            onClick={() => this.seleccionarFiltrados(filas)}
          >
            Seleccionar filtrados ({seleccionablesFiltrados})
          </ButtonItem>
          <ButtonItem
            type="default"
            size="small"
            blanco
            disabled={seleccionados.length === 0}
            onClick={() => this.setState({ seleccionados: [] })}
          >
            Limpiar selección
          </ButtonItem>
          <span style={{ fontSize: 12, color: "#595959" }}>
            {seleccionados.length} empleado(s) seleccionado(s)
          </span>
        </Space>

        <TableItem
          rowKey={(fila) => fila._id}
          columns={columnas}
          dataSource={filas}
          size="small"
          defaultPageSize={50}
          rowClassName={(_, indice) => (indice % 2 === 0 ? "zebra-gris" : "default")}
          rowSelection={{
            selectedRowKeys: seleccionados,
            // Sin esto antd suelta de la selección a quien deja de verse al cambiar
            // un filtro, y la selección no se podría armar por partes.
            preserveSelectedRowKeys: true,
            onChange: (keys) => this.setState({ seleccionados: keys }),
            getCheckboxProps: (fila) => ({ disabled: fila.cubierto }),
          }}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
          <ButtonItem
            type="primary"
            loading={revisando}
            disabled={!turnoId || !rangoCompleto || seleccionados.length === 0 || revisando}
            onClick={() => this.revisar()}
          >
            Revisar ({seleccionados.length} empleado(s))
          </ButtonItem>
        </div>
      </>
    );
  }

  // ─── Revisión y envío ──────────────────────────────────────────────────────

  turnoElegido() {
    return etiquetaTurno(this.state.catalogos?.turnoPorId.get(this.state.turnoId));
  }

  confirmarAplicar(resumen) {
    const { inicio, fin } = this.rango();
    Modal.confirm({
      title: "Aplicar asignaciones de turno",
      width: 520,
      content: (
        <div>
          <p>
            Turno <b>{this.turnoElegido()}</b> del <b>{claveATexto(inicio)}</b> al{" "}
            <b>{claveATexto(fin)}</b>: se van a crear <b>{resumen.crear}</b>, reactivar{" "}
            <b>{resumen.reactivar}</b> y extender <b>{resumen.extender}</b> asignación(es).
          </p>
          {resumen.recortes + resumen.deshabilitados > 0 && (
            <p>
              Para que no se encimen, se recortan <b>{resumen.recortes}</b> y se deshabilitan{" "}
              <b>{resumen.deshabilitados}</b> asignación(es) de otros turnos.
            </p>
          )}
          <p style={{ marginBottom: 0 }}>
            Los reportes de asistencia usan el turno nuevo desde el {claveATexto(inicio)}; las
            fechas anteriores no cambian.
          </p>
        </div>
      ),
      okText: "Sí, aplicar",
      cancelText: "Cancelar",
      // Sin regresar la promesa: antd dejaría el confirm abierto durante todo el
      // envío, tapando la barra de progreso.
      onOk: () => {
        this.aplicar();
      },
    });
  }

  // Bloques en serie: cada empleado es una transacción en el servidor, así que no
  // va todo en una petición. El servidor vuelve a planear con lo que haya en la
  // BD en ese momento.
  async aplicar() {
    // Dos confirms abiertos con doble clic no deben mandar el lote dos veces.
    if (this.enviandoLote) return;
    this.enviandoLote = true;

    const { turnoId, plan } = this.state;
    const { inicio, fin } = this.rango();

    const ids = plan.filter((fila) => SE_APLICAN.has(fila.operacion)).map((fila) => fila.empleadoId);
    const bloques = [];
    for (let i = 0; i < ids.length; i += TAMANO_BLOQUE) bloques.push(ids.slice(i, i + TAMANO_BLOQUE));

    const total = ids.length;
    const agregado = {
      numeroCreados: 0,
      numeroEditados: 0,
      numeroAjustados: 0,
      resultados: [],
      procesadas: 0,
      totalFilas: total,
    };

    this.setState({ enviando: true, progreso: { hechas: 0, total } });

    for (const bloque of bloques) {
      let res;
      try {
        res = await axios.post(
          "asignacionesTurnos/asignarTurnosMasivo",
          {
            empresaId: this.props.empresaId,
            turnoId,
            inicio,
            fin,
            empleadoIds: bloque,
            simular: false,
          },
          { headers: this.props.headersToken }
        );
      } catch (err) {
        agregado.incompleto = true;
        agregado.mensajeError = err.response?.data?.message
          ? err.response.data.message
          : err.response?.status
            ? `El servidor respondió ${err.response.status}`
            : `Error de red: ${err.message}`;
        break;
      }

      if (res.data.success !== 2) {
        agregado.incompleto = true;
        agregado.mensajeError = res.data.message || "El servidor no pudo aplicar las asignaciones";
        break;
      }

      agregado.numeroCreados += res.data.numeroCreados ?? 0;
      agregado.numeroEditados += res.data.numeroEditados ?? 0;
      agregado.numeroAjustados += res.data.numeroAjustados ?? 0;
      if (Array.isArray(res.data.resultados)) agregado.resultados.push(...res.data.resultados);
      agregado.procesadas += bloque.length;

      this.setState({ progreso: { hechas: agregado.procesadas, total } });
    }

    this.enviandoLote = false;
    this.setState({ enviando: false, progreso: null });

    if (agregado.numeroCreados + agregado.numeroEditados > 0) {
      message.success(
        `Se crearon ${agregado.numeroCreados} y se reactivaron o extendieron ${agregado.numeroEditados} asignaciones de turno` +
          (agregado.numeroAjustados
            ? `; se ajustaron ${agregado.numeroAjustados} de otros turnos`
            : "")
      );
    }

    if (agregado.incompleto) {
      message.error(
        `${agregado.mensajeError}. Se procesaron ${agregado.procesadas} de ${total}. Vuelve a abrir "Agregar Varias": los que ya quedaron aparecen como asignados.`,
        10
      );
    }

    // Sólo el detalle de los renglones que el servidor rechazó: el aviso de envío
    // incompleto de ese resumen habla de la plantilla Excel, y aquí no aplica.
    mostrarResumenImportacion(
      { ...agregado, incompleto: false },
      { entidad: "asignaciones de turno" }
    );

    this.props.cerrarAgregarAsignacionesTurnosMasivas();
  }

  renderRevision() {
    const { plan, enviando, progreso } = this.state;
    const { inicio, fin } = this.rango();
    const resumen = resumirPlan(plan);
    const porAplicar = resumen.crear + resumen.reactivar + resumen.extender;

    const columnas = [
      {
        title: "Operación",
        dataIndex: "operacion",
        key: "operacion",
        width: 110,
        render: (valor) => {
          const operacion = OPERACIONES[valor];
          return operacion ? <Tag color={operacion.color}>{operacion.texto}</Tag> : valor;
        },
      },
      { title: "Empleado", dataIndex: "empleadoNombre", key: "empleadoNombre" },
      {
        title: "Fechas",
        dataIndex: "inicio",
        key: "fechas",
        width: 190,
        render: (_, fila) =>
          fila.operacion === "error" ? "" : `${claveATexto(fila.inicio)} al ${claveATexto(fila.fin)}`,
      },
      {
        title: "Otros turnos",
        dataIndex: "ajustes",
        key: "ajustes",
        render: (ajustes, fila) =>
          SE_APLICAN.has(fila.operacion)
            ? (ajustes || []).map((ajuste) =>
                ajuste.accion === "recortar" ? (
                  <Tooltip
                    key={ajuste.asignacionId}
                    title={`Era del ${claveATexto(ajuste.inicio)} al ${claveATexto(ajuste.finAnterior)}`}
                  >
                    <Tag color="orange">
                      Recorta {ajuste.turnoNombre} al {claveATexto(ajuste.finNuevo)}
                    </Tag>
                  </Tooltip>
                ) : (
                  <Tooltip
                    key={ajuste.asignacionId}
                    title={`Del ${claveATexto(ajuste.inicio)} al ${claveATexto(ajuste.finAnterior)}: queda completo dentro de las fechas nuevas`}
                  >
                    <Tag color="red">Deshabilita {ajuste.turnoNombre}</Tag>
                  </Tooltip>
                )
              )
            : null,
      },
      {
        title: "Detalle",
        dataIndex: "motivo",
        key: "motivo",
        render: (motivo, fila) => (
          <>
            {motivo}
            {(fila.avisos || []).map((aviso, i) => (
              <div key={i} style={{ color: "#d46b08" }}>
                {aviso}
              </div>
            ))}
          </>
        ),
      },
    ];

    return (
      <>
        <Alert
          type={resumen.error || resumen.conAviso ? "warning" : "info"}
          showIcon
          style={{ marginBottom: 10 }}
          message={
            <span>
              <b>{this.turnoElegido()}</b> del {claveATexto(inicio)} al {claveATexto(fin)}:{" "}
              <b>{resumen.crear}</b> alta(s) · <b>{resumen.reactivar}</b> reactivada(s) ·{" "}
              <b>{resumen.extender}</b> extendida(s) · <b>{resumen.sinCambios}</b> ya asignada(s),
              se omiten · <b>{resumen.error}</b> con error, no se aplican
            </span>
          }
          description={
            resumen.recortes + resumen.deshabilitados + resumen.conAviso > 0 ? (
              <span>
                Se recortan <b>{resumen.recortes}</b> y se deshabilitan{" "}
                <b>{resumen.deshabilitados}</b> asignación(es) de otros turnos.
                {resumen.conAviso > 0 && (
                  <>
                    {" "}
                    <b>{resumen.conAviso}</b> empleado(s) se quedan sin turno después del{" "}
                    {claveATexto(fin)}.
                  </>
                )}
              </span>
            ) : undefined
          }
        />

        {progreso && (
          <Progress
            percent={progreso.total ? Math.round((progreso.hechas / progreso.total) * 100) : 0}
            format={() => `${progreso.hechas} / ${progreso.total}`}
            style={{ marginBottom: 10 }}
          />
        )}

        <TableItem
          rowKey={(fila) => fila.key}
          columns={columnas}
          dataSource={plan}
          size="small"
          defaultPageSize={50}
          rowClassName={(_, indice) => (indice % 2 === 0 ? "zebra-gris" : "default")}
        />

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
          <ButtonItem
            type="default"
            blanco
            disabled={enviando}
            onClick={() => this.setState({ etapa: "seleccion", plan: [] })}
          >
            Atrás
          </ButtonItem>
          <ButtonItem
            type="primary"
            loading={enviando}
            disabled={porAplicar === 0 || enviando}
            onClick={() => this.confirmarAplicar(resumen)}
          >
            Aplicar ({porAplicar})
          </ButtonItem>
        </div>
      </>
    );
  }

  render() {
    return (
      <ModalItem
        title="Agregar Varias Asignaciones de Turno"
        visible={this.props.visible}
        spinning={this.state.cargando}
        onCancel={() => this.cerrar()}
        closable={!this.state.enviando}
        maskClosable={false}
        width={window.innerWidth > 1000 ? "80%" : "95%"}
        paddingTop
      >
        {this.state.etapa === "revision" ? this.renderRevision() : this.renderSeleccion()}
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(AgregarAsignacionesTurnosMasivas);
